import { ModeProvider } from 'canoe';
import { ENDPOINTS, useActions } from 'hammock';
import { nanoid } from 'nanoid';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { ContentType } from '@candu-ai/content/types';
import { AddSnapshotModal } from '@candu-ai/placement/content-placements';
import { ContentPlacement } from '@candu-ai/placement/types';
import { SnapshotListModal } from '@candu-ai/snapshots/components';
import { Snapshot } from '@candu-ai/snapshots/types';
import { Flags, useAuthentication, useFlags, useLocalStorage, useToggle } from 'commons/hooks';
import { useActiveTooltip } from 'craft/components/Tooltip';
import { PlacementsForm } from 'craft/containers/Toolbox/tabs/Placements/PlacementsForm';
import { PlacementsList } from 'craft/containers/Toolbox/tabs/Placements/components/PlacementsList';
import { useCraftPropsContext } from 'craft/contexts';
import { useTooltipPlacement } from 'craft/hooks';
import { setSnapshot, useToolboxTab } from 'craft/state';
import { usePlacements } from 'craft/state/placements';
import { useCurrentSnapshot } from 'craft/state/snapshots';
import { getSelectorFromIframe } from 'craft/utils/snapshot-iframe';

import { ActionButtons } from './components/ActionButtons';
import { RemoveSnapshotModal, RetakeSnapshotModal } from './components/modals';
import { Wrapper } from './components/ui';
import {
  cleanUpDisconnectedSnapshots,
  getDefaultPlacement,
  removeSnapshot,
  retakeSnapshot,
  saveNewPlacement,
  updateExistingPlacement,
} from './utils';

export const Placements = React.memo(() => {
  const [retakeSnapshotModalOpen, toggleRetakeSnapshotModalOpen] = useToggle();
  const [removeSnapshotModalOpen, toggleRemoveSnapshotModalOpen] = useToggle();
  const [addSnapshotModalOpen, toggleAddSnapshotModalOpen] = useToggle();
  const { [Flags.EXISTING_SNAPSHOTS]: withSnapshotChooser } = useFlags();
  const [snapshotListModalOpen, toggleSnapshotListModalOpen] = useToggle();
  const { contentMetadata } = useCraftPropsContext();
  const dispatch = useDispatch();

  const { getAll: getSnapshots, delete: deleteSnapshot } = useActions(ENDPOINTS.snapshots);
  const { delete: deletePlacement, update: updatePlacement, create: createPlacement } = useActions(
    ENDPOINTS.contentPlacements,
  );
  const [searchParams] = useSearchParams();
  const { applicationId } = useAuthentication();
  const { snapshot, placement: snapshotPlacement } = useCurrentSnapshot();
  const { placements } = usePlacements();
  const activeTooltip = useActiveTooltip();
  const { placement: tooltipPlacement } = useTooltipPlacement(activeTooltip?.id);
  const { forcePlacementsTab } = useToolboxTab();
  const [, setAddSnapshotModalCount] = useLocalStorage('candu.snapshots.addSnapshotModalCount');
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);

  const snapshotURL = useMemo(() => snapshot?.pageUrl ?? '', [snapshot]);

  if (!contentMetadata) {
    throw new Error('ContentMetadata required to render placements tab');
  }

  const isTooltipContent = useMemo(
    () => [ContentType.Tour, ContentType.HotspotGroup].indexOf(contentMetadata.contentType) >= 0,
    [contentMetadata.contentType],
  );

  const contentSnapshots = useMemo(
    () => snapshots.filter((snap) => snap.contentId === contentMetadata.id),
    [snapshots, contentMetadata.id],
  );

  const isInline = contentMetadata.contentType === ContentType.Inline;

  const [editingPlacement, setEditingPlacement] = useState<ContentPlacement | undefined>(
    !isInline ? placements?.[0] : undefined,
  );

  useEffect(() => {
    getSnapshots().then((res: Snapshot[]) => setSnapshots(res));
  }, [contentMetadata.id]);

  useEffect(() => {
    // if we chose a snapshot while creating a new placement,
    // or if the placement selector is empty,
    // or if we load an inline placement with a selector not visible on page,
    // make sure we go back to editing mode after loading the snapshot
    if (
      snapshotPlacement &&
      (!snapshotPlacement.selector ||
        (isInline && !getSelectorFromIframe(snapshotPlacement.selector)))
    ) {
      setEditingPlacement(snapshotPlacement);
    }
  }, [snapshotPlacement, isInline]);

  const onCreateSnapshotPlacement = useCallback(
    (snapshotData?: Snapshot) => {
      const newPlacement = {
        ...getDefaultPlacement(contentMetadata, snapshotData?.pageUrl ?? ''),
        snapshotId: snapshotData?.id,
      };

      dispatch(setSnapshot(snapshotData, newPlacement));
    },
    [contentMetadata],
  );

  const onCreateSnapshot = (targetUrl: string, removeSnapshotId?: string) => {
    if (!applicationId || targetUrl === '' || !contentMetadata.id) {
      return;
    }
    retakeSnapshot({
      applicationId,
      contentId: contentMetadata.id,
      targetUrl,
      placement: editingPlacement,
      removeSnapshotId,
    });

    if (removeSnapshotId) {
      toggleRetakeSnapshotModalOpen();
    } else {
      toggleSnapshotListModalOpen();
    }
  };

  const onRetakeSnapshot = (targetUrl: string) => {
    onCreateSnapshot(targetUrl, snapshot?.id);
  };

  const onRemoveSnapshot = async () => {
    if (!snapshot) {
      return;
    }
    await removeSnapshot({
      deleteSnapshot,
      deletePlacement,
      snapshotId: snapshot.id,
      placement: editingPlacement,
      contentId: contentMetadata.id,
    });

    // Snapshot cleanup!
    await cleanUpDisconnectedSnapshots({
      contentType: contentMetadata.contentType,
      placements,
      snapshots: contentSnapshots,
      contentId: contentMetadata.id,
      deleteSnapshot,
      excludeSnapshotId: editingPlacement?.snapshotId,
    });

    dispatch(setSnapshot(undefined, undefined));
    setEditingPlacement(!isInline ? placements?.[0] : undefined);

    toggleRemoveSnapshotModalOpen();
    setAddSnapshotModalCount(3);
  };

  const shouldShowList = isInline && !editingPlacement;

  // Don't allow snapshot actions when creating a new tooltip placement
  const shouldAllowSnapshotChange = !isTooltipContent || !!tooltipPlacement?.selector;

  return (
    <Wrapper>
      {shouldShowList && (
        // we might have an undefined content placement, but we don't always want to show this list explicitly
        // set to null when it's inline content so we don't trigger the wrong view if content placement is missing
        <PlacementsList
          contentId={contentMetadata.id}
          setEditingPlacement={setEditingPlacement}
          onAddSnapshot={
            withSnapshotChooser
              ? () => toggleSnapshotListModalOpen()
              : () => toggleAddSnapshotModalOpen()
          }
          onAddSnapshotPlacement={() => onCreateSnapshotPlacement(snapshot)}
        />
      )}

      {!shouldShowList && (
        <PlacementsForm
          snapshotURL={snapshotURL}
          contentMetadata={contentMetadata}
          snapshot={snapshot}
          activePlacement={editingPlacement}
          onGoBack={
            contentMetadata.contentType === ContentType.Inline
              ? () => {
                  setEditingPlacement(undefined);
                }
              : undefined
          }
        />
      )}

      {contentMetadata.personalisationMode !== 'Experimentation' && !shouldShowList && (
        <ActionButtons
          onRetakeClick={
            snapshot && shouldAllowSnapshotChange
              ? () => toggleRetakeSnapshotModalOpen()
              : undefined
          }
          onAddClick={
            !snapshot && withSnapshotChooser && shouldAllowSnapshotChange
              ? () => toggleAddSnapshotModalOpen()
              : undefined
          }
          onOpenListClick={
            withSnapshotChooser && shouldAllowSnapshotChange
              ? () => toggleSnapshotListModalOpen()
              : undefined
          }
          onRemoveClick={
            snapshot && !forcePlacementsTab && (!isTooltipContent || contentSnapshots.length > 1)
              ? () => toggleRemoveSnapshotModalOpen()
              : undefined
          }
        />
      )}

      {/* PLACEMENTS CTA MODALS */}
      <ModeProvider value={{ mode: 'Light' }}>
        {retakeSnapshotModalOpen && (
          <RetakeSnapshotModal
            onClose={toggleRetakeSnapshotModalOpen}
            onSubmit={({ url }) => onRetakeSnapshot(url)}
            snapshotURL={snapshotURL}
          />
        )}
        {addSnapshotModalOpen && (
          <AddSnapshotModal
            onClose={toggleAddSnapshotModalOpen}
            contentId={contentMetadata.id}
            activePlacement={editingPlacement}
            forceOpen
          />
        )}
        {snapshotListModalOpen && (
          <SnapshotListModal
            onClose={toggleSnapshotListModalOpen}
            onSnapshotChoose={async (newSnapshot: Snapshot) => {
              if (editingPlacement) {
                const placementExists = !!editingPlacement.id;
                const placementSnapshotId = editingPlacement.snapshotId;
                const editedPlacement = {
                  ...editingPlacement,
                  snapshotId: newSnapshot.id,
                };

                if (placementExists) {
                  await updateExistingPlacement({
                    updatePlacement,
                    contentId: contentMetadata.id,
                    updatedPlacement: editedPlacement,
                    hideNotification: true,
                  }).then(() => {
                    dispatch(setSnapshot(newSnapshot, editedPlacement));
                    searchParams.set('editSnapshotPlacementId', newSnapshot.id);
                    toggleSnapshotListModalOpen();
                  });
                } else {
                  const newPlacement = {
                    ...editedPlacement,
                    id: nanoid(6),
                    ...(editedPlacement.urls?.[0]
                      ? {
                          urls: [
                            {
                              ...editedPlacement.urls[0],
                              operation: {
                                ...editedPlacement.urls[0].operation,
                                operand: newSnapshot.pageUrl,
                              },
                            },
                          ],
                        }
                      : {}),
                  };
                  await saveNewPlacement({
                    createPlacement,
                    contentId: contentMetadata.id,
                    newPlacement,
                    hideNotification: true,
                  }).then(() => {
                    dispatch(setSnapshot(newSnapshot, newPlacement));
                    setEditingPlacement(newPlacement);
                    searchParams.set('editSnapshotPlacementId', newSnapshot.id);
                    toggleSnapshotListModalOpen();
                  });
                }

                // remove snapshot that was tied to this placement
                if (placementSnapshotId) {
                  await removeSnapshot({
                    deleteSnapshot,
                    snapshotId: placementSnapshotId,
                    contentId: contentMetadata.id,
                    hideNotification: true,
                  });
                }
              } else {
                toggleSnapshotListModalOpen();

                // if no placement was being edited at the time of choosing the snapshot,
                // set active snapshot with a new default placement and go into click to edit mode
                // (e.g. when creating a snapshot from empty placements list)
                onCreateSnapshotPlacement(newSnapshot);
              }
            }}
            onSnapshotCreate={(url) => onCreateSnapshot(url)}
            snapshots={snapshots}
            selectedSnapshot={snapshot}
            id={contentMetadata.id}
          />
        )}
        {removeSnapshotModalOpen && (
          <RemoveSnapshotModal
            onClose={toggleRemoveSnapshotModalOpen}
            onSubmit={() => onRemoveSnapshot()}
          />
        )}
      </ModeProvider>
    </Wrapper>
  );
});
